
import { useState, useEffect, useRef } from "react";
import ProjectCard from "./ProjectCard";
import AnimatedText from "./AnimatedText";
import { cn } from "@/lib/utils";
import { siteConfig } from "@/config";
import { useIsMobile } from "@/hooks/use-mobile";

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [isVisible, setIsVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const autoAnimationTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle visibility based on scroll position
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Mobile automatic focus cycling
  useEffect(() => {
    if (isMobile && filteredProjects.length > 0 && isVisible) {
      // Start auto-cycling through projects on mobile
      const startAutoAnimation = () => {
        let currentIndex = 0;
        
        const cycleProjects = () => {
          setActiveIndex(currentIndex);
          currentIndex = (currentIndex + 1) % filteredProjects.length;
          
          // Clear existing timer
          if (autoAnimationTimerRef.current) {
            clearTimeout(autoAnimationTimerRef.current);
          }
          
          // Set next timer
          autoAnimationTimerRef.current = setTimeout(cycleProjects, 2500);
        };
        
        cycleProjects();
      };
      
      startAutoAnimation();
      
      return () => {
        if (autoAnimationTimerRef.current) {
          clearTimeout(autoAnimationTimerRef.current);
        }
      };
    }
  }, [isMobile, isVisible, activeFilter]);

  const projectCategories = [
    { id: "all", label: "All" },
    { id: "saas", label: "SaaS" },
    { id: "ai", label: "AI" },
    { id: "startup", label: "Startup" },
  ];

  const filteredProjects =
    activeFilter === "all"
      ? siteConfig.projects.items
      : siteConfig.projects.items.filter((project) => project.category.includes(activeFilter));

  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    setActiveIndex(null); // Reset active index when filter changes
  };

  // Manual hover handling
  const handleProjectHover = (index: number | null) => {
    if (!isMobile) {
      setActiveIndex(index);
    }
  };

  if (!siteConfig.projects.showSection) {
    return null;
  }

  return (
    <section id="projects" className="py-20 md:py-32" ref={sectionRef}>
      <div className="container mx-auto px-4">
        <div className={cn(
          "flex flex-col md:flex-row justify-between items-start md:items-end mb-12 md:mb-20 transition-all duration-1000",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        )}>
          <h2 className="font-display text-5xl md:text-6xl tracking-tight mb-6 md:mb-0">
            <AnimatedText text={siteConfig.projects.title} />
          </h2>

          <div className="flex flex-wrap gap-4">
            {projectCategories.map((category, index) => (
              <button
                key={category.id}
                className={cn(
                  "text-sm uppercase tracking-wider px-4 py-2 border transition-all duration-500 relative overflow-hidden group",
                  activeFilter === category.id
                    ? "bg-white text-black"
                    : "border-white text-white hover:bg-white hover:text-black"
                )}
                style={{ transitionDelay: `${index * 100}ms` }}
                onClick={() => handleFilterChange(category.id)}
              >
                <span className="relative z-10">{category.label}</span>
                {activeFilter !== category.id && (
                  <span className="absolute inset-0 bg-white transform scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300"></span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div 
          ref={projectsRef} 
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8"
        >
          {filteredProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              id={project.id}
              title={project.title}
              description={project.description}
              image={project.image}
              tags={project.tags}
              link={project.link}
              delay={300 + index * 150}
              isActive={isMobile ? activeIndex === index : undefined}
              onMouseEnter={() => handleProjectHover(index)}
              onMouseLeave={() => handleProjectHover(null)}
            />
          ))}
        </div>

        <div className={cn(
          "mt-12 flex justify-center transition-all duration-700",
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        )} style={{ transitionDelay: "800ms" }}>
          <a 
            href="#contact" 
            className="inline-block border border-white px-8 py-3 uppercase text-sm tracking-widest transition-all duration-300 relative group overflow-hidden"
          >
            <span className="relative z-10 transition-colors duration-300 group-hover:text-black">Discuss a Project</span>
            <span className="absolute inset-0 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Projects;
